import { useLazyQuery } from '@apollo/client'
import { useRef, useState } from 'react'

import { EVALUATE_PROMOTION_QUERY } from '../../../../graphql/promotions'
import {
  AppliedPromotion,
  PromotionEvaluation,
  PromotionIntent,
  SkippedPromotion
} from '../../../../types/cart'
import { selectRefusal } from './refusal'

type Verdict = {
  /** The promotion the entered code applied, if it did. */
  appliedForCode?: AppliedPromotion
  /**
   * The cart total FOR THE CHECKED CODE, from the service. Never summed client-side.
   *
   * Fenced on `isCurrent` exactly like `appliedForCode`, and that fencing is the fix for a
   * defect this hook shipped with: the total was read from raw `data`, on the reasoning
   * that justified it for the property's own discounts. That reasoning does not carry.
   * What the property applies is independent of what the operator typed; a total that
   * INCLUDES the typed code is not. So Remove reverted the applied row and left the total
   * showing the removed code's figure, and an operator could quote a guest a discount that
   * no longer applied. Pinned by 'drops the total when the code behind it is removed'.
   */
  totalDiscountMinor?: string
  currency?: string
  /** Why it did not, if it did not. */
  refusal?: SkippedPromotion
  /** The CURRENT check failed — distinct from the code being refused. */
  failed: boolean
  /** The CURRENT evaluation returned neither a code discount nor a refusal reason. */
  noChange: boolean
  checking: boolean
  /** Code accepted by the latest completed request for this exact cart snapshot. */
  validatedCode?: string
  check: (code: string) => void
}

/**
 * Checking a code against a cart, and keeping the answer attached to the code that
 * produced it.
 *
 * The fencing is the reason this is a hook rather than four lines in the component. A
 * verdict belongs to the request that produced it, so `checkedCode` — the code actually
 * SENT — is tracked separately from what is in the field now, and every part of the
 * verdict is gated on the two matching. Typing must invalidate the previous answer:
 * leaving "applied" beside an edited code attributes a passed check to a string nobody
 * checked.
 *
 * Derived rather than stored in an effect. An effect that clears state on every code
 * change is a second source of truth for the same fact, and the two drift.
 *
 * `!checking` is kept and is probably REDUNDANT with this client. Four attempts to build a
 * case where removing it changes any outcome all failed — the last with a real Apollo
 * client, a delayed mock, and a harness that changes the code WITHOUT remounting, which is
 * the actual sequence rather than an imitation of it. Likely reason: the client clears
 * `data` when variables change, so during a second in-flight request there is no previous
 * response left to mis-attribute. Left in place because it costs nothing and states the
 * intent, and because that property would otherwise rest entirely on undocumented client
 * behaviour. Marked UNVERIFIED rather than described as a guard: no test pins it.
 */
export function usePromotionEvaluation(
  cartID: string,
  intent: PromotionIntent,
  cartKey = cartID
): Verdict {
  const [checkedCode, setCheckedCode] = useState<string | undefined>()
  const [lastSeenCode, setLastSeenCode] = useState<string | undefined>(
    (intent.code ?? '').trim()
  )
  const [lastSeenCartKey, setLastSeenCartKey] = useState(cartKey)
  const [validatedCode, setValidatedCode] = useState<string | undefined>()
  const requestGeneration = useRef(0)
  const [evaluate, { data, loading: checking, error }] = useLazyQuery<{
    evaluatePromotion: PromotionEvaluation
  }>(EVALUATE_PROMOTION_QUERY, { fetchPolicy: 'network-only' })

  const pendingCode = (intent.code ?? '').trim()

  if (cartKey !== lastSeenCartKey) {
    setLastSeenCartKey(cartKey)
    setCheckedCode(undefined)
    setValidatedCode(undefined)
    requestGeneration.current += 1
  }

  // Invalidate the moment the code moves AWAY from what was checked, rather than only
  // while it differs.
  //
  // `isCurrent` was pure string equality, and `checkedCode` was never reset — so a code
  // that was applied, removed, and then retyped re-satisfied the comparison and re-showed
  // Apollo's LAST response with no new request made. The figure in it was computed against
  // the cart as it stood before the removal, and it reappeared presented as current.
  //
  // Adjusting state during render rather than in an effect, which is the pattern React
  // sanctions for deriving from changed props and what the note above already argues for:
  // an effect here would be a second source of truth for the same fact, and the two drift.
  // React re-runs this render immediately with the updated state, before committing.
  if (pendingCode !== lastSeenCode) {
    setLastSeenCode(pendingCode)
    if (pendingCode !== checkedCode) {
      setCheckedCode(undefined)
      setValidatedCode(undefined)
      requestGeneration.current += 1
    }
  }

  const isCurrent =
    checkedCode !== undefined && checkedCode === pendingCode && !checking

  const result = isCurrent ? data?.evaluatePromotion : undefined
  const appliedForCode = result?.applied.find(
    (promotion) => promotion.distribution === 'CodeEntry'
  )
  const refusal = selectRefusal(result, appliedForCode !== undefined)
  const failed = Boolean(error) && isCurrent

  return {
    appliedForCode,
    // `result`, not `data` — see the field's own note. The property's discounts are read
    // by usePropertyDiscounts, which is not fenced, because they do not depend on the code.
    totalDiscountMinor: result?.totalDiscountMinor,
    currency: result?.currency,
    refusal,
    failed,
    noChange: Boolean(result && !appliedForCode && !refusal && !failed),
    checking,
    validatedCode:
      isCurrent && validatedCode === pendingCode ? validatedCode : undefined,
    check: (code: string) => {
      if (code === '') return
      const generation = requestGeneration.current + 1
      requestGeneration.current = generation
      setCheckedCode(code)
      setValidatedCode(undefined)
      const request = evaluate({
        variables: {
          cartID,
          intent: { applyAutomatic: intent.applyAutomatic, code }
        }
      })

      // Apollo exposes one shared `data` slot for a lazy query. The promise belongs to
      // this invocation, so use it to bind acceptance to the request that produced it;
      // an older request completing later cannot validate the newer field value.
      if (request) {
        void request
          .then(({ data: responseData }) => {
            const accepted = responseData?.evaluatePromotion.applied.some(
              (promotion) => promotion.distribution === 'CodeEntry'
            )
            if (requestGeneration.current === generation && accepted) {
              setValidatedCode(code)
            }
          })
          .catch(() => {
            if (requestGeneration.current === generation) {
              setValidatedCode(undefined)
            }
          })
      }
    }
  }
}
